/**
 * Dashboard — the admin "people layer": per-user activity, group→role mapping,
 * platform state. Model-level metering and infra health live in the AWS
 * consoles (Mantle usage, CloudWatch); this page links out rather than
 * duplicating them.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Panel, SubHeading } from '../components/Panel';
import { downloadUsageCsv } from '../lib/usageCsv';
import {
  ALL_MODELS,
  ALL_PEOPLE,
  filterUsage,
  isFiltered,
  modelVendor,
  usageModels,
  usagePeople,
} from '../lib/usageFilter';

interface UserActivity {
  actor: string;
  groups: string[];
  events: number;
  denied: number;
  first_seen: string;
  last_seen: string;
}

interface GroupMapping {
  source: string;
  name: string;
  admin: boolean;
}

interface Overview {
  users: UserActivity[];
  group_mappings: GroupMapping[];
  admin_roles_note: string;
  model_provider: string;
  model_id: string;
  auth_provider: string;
  chain_valid: boolean;
  total_events: number;
}

interface Suspension {
  email: string;
  reason: string;
  suspended_by: string;
  suspended_at: string;
}

interface PortalUsageRow {
  actor: string;
  model_id: string;
  requests: number;
  failed: number;
  incomplete: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  last_used: string | null;
}

interface ToolUsageRow {
  person: string;
  model: string | null;
  requests: number;
  refused: number;
  last_used: string | null;
}

interface UsageReport {
  start: string;
  end: string;
  portal: PortalUsageRow[];
  tools: ToolUsageRow[];
  tools_as_of: string | null;
  tools_error: string | null;
  cost_by_person: Record<string, number>;
  cost_by_vendor: Record<string, Record<string, number>>;
  portal_cost: number | null;
  other_cost: number | null;
  cost_as_of: string | null;
  cost_error: string | null;
}

interface PersonTotals {
  person: string;
  requests: number;
  problems: number;
  input: number;
  output: number;
  models: Map<string, number>;
  last: string;
}

function byPerson<T>(
  rows: T[],
  key: (r: T) => string,
  model: (r: T) => string,
  add: (t: PersonTotals, r: T) => void,
  last: (r: T) => string | null,
): PersonTotals[] {
  const people = new Map<string, PersonTotals>();
  for (const r of rows) {
    const k = key(r);
    const t =
      people.get(k) ??
      { person: k, requests: 0, problems: 0, input: 0, output: 0, models: new Map(), last: '' };
    add(t, r);
    t.models.set(model(r), (t.models.get(model(r)) ?? 0) + 1);
    const l = last(r) ?? '';
    if (l > t.last) t.last = l;
    people.set(k, t);
  }
  return [...people.values()].sort((a, b) => b.requests - a.requests);
}

const fmtN = (n: number): string => n.toLocaleString();
const fmtUsd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const AWS_LINKS = [
  {
    label: 'Model usage & tokens',
    note: 'Bedrock console → Mantle → Usage',
    href: 'https://us-gov-west-1.console.amazonaws-us-gov.com/bedrock-mantle/projects/default/insights?tab=inference',
  },
  {
    label: 'System health',
    note: 'CloudWatch dashboard (latency, errors, database)',
    href: 'https://console.amazonaws-us-gov.com/cloudwatch/home?region=us-gov-west-1#dashboards/dashboard/tridsys-prod',
  },
  {
    label: 'AI platform admin activity',
    note: 'CloudTrail — who browsed models, projects and settings, by name',
    href: 'https://console.amazonaws-us-gov.com/cloudtrailv2/home?region=us-gov-west-1#/events?EventSource=bedrock-mantle.amazonaws.com',
  },
  {
    label: 'Raw audit logs (export)',
    note: 'CloudTrail data-event files in S3 — every AI tool call per person. For export and forensics; querying needs Athena',
    href: 'https://console.amazonaws-us-gov.com/s3/buckets/tridsys-prod-cloudtrail-logs-705494509498?region=us-gov-west-1&prefix=AWSLogs/705494509498/CloudTrail/&showversions=false',
  },
];

function fmtTs(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

function StatTile({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="rounded-lg border border-steel-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-steel-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-navy-900">{value}</p>
    </div>
  );
}

type Period = '7' | '30' | '90' | '365' | 'custom';

function Usage(): ReactNode {
  const [period, setPeriod] = useState<Period>('30');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [person, setPerson] = useState(ALL_PEOPLE);
  const [model, setModel] = useState(ALL_MODELS);
  const custom = period === 'custom';
  const query = custom ? `start=${from}&end=${to}` : `days=${period}`;
  const report = useQuery({
    queryKey: ['admin-usage', query],
    queryFn: () => api.get<UsageReport>(`/admin/usage?${query}`),
    enabled: !custom || (from !== '' && to !== '' && from <= to),
  });
  const filter = { person, model };
  const filtered = isFiltered(filter);
  const people = report.data ? usagePeople(report.data) : [];
  const models = report.data ? usageModels(report.data) : [];
  const data = report.data ? filterUsage(report.data, filter) : undefined;
  const qc = useQueryClient();
  const refreshCost = useMutation({
    mutationFn: () => api.post<unknown>('/admin/billing/refresh'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-usage'] }),
  });
  const peopleCost = data
    ? Object.values(data.cost_by_person).reduce((sum, c) => sum + c, 0)
    : 0;
  const totalCost = peopleCost + (data?.portal_cost ?? 0) + (data?.other_cost ?? 0);

  const tools = byPerson(
    data?.tools ?? [],
    (r) => r.person,
    (r) => r.model ?? 'unknown',
    (t, r) => {
      t.requests += r.requests;
      t.problems += r.refused;
    },
    (r) => r.last_used,
  );
  const portal = byPerson(
    data?.portal ?? [],
    (r) => r.actor,
    (r) => r.model_id,
    (t, r) => {
      t.requests += r.requests;
      t.problems += r.failed + r.incomplete;
      t.input += r.input_tokens + r.cache_read_tokens + r.cache_write_tokens;
      t.output += r.output_tokens;
    },
    (r) => r.last_used,
  );

  const actions = (
    <>
      <select
        aria-label="Person"
        value={person}
        onChange={(e) => setPerson(e.target.value)}
        className="rounded border border-steel-300 px-2 py-0.5 text-xs"
      >
        <option value={ALL_PEOPLE}>All people</option>
        {people.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
      <select
        aria-label="Model"
        value={model}
        onChange={(e) => setModel(e.target.value)}
        className="rounded border border-steel-300 px-2 py-0.5 text-xs"
      >
        <option value={ALL_MODELS}>All models</option>
        {models.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        aria-label="Period"
        value={period}
        onChange={(e) => setPeriod(e.target.value as Period)}
        className="rounded border border-steel-300 px-2 py-0.5 text-xs"
      >
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="365">Last 12 months</option>
        <option value="custom">Custom dates</option>
      </select>
      {custom && (
        <>
          <input
            aria-label="From"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded border border-steel-300 px-2 py-0.5 text-xs"
          />
          <input
            aria-label="To"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded border border-steel-300 px-2 py-0.5 text-xs"
          />
        </>
      )}
      <button
        type="button"
        disabled={refreshCost.isPending}
        onClick={() => refreshCost.mutate()}
        className="rounded border border-steel-300 px-2 py-0.5 text-xs text-navy-800 hover:border-navy-400 disabled:opacity-50"
      >
        {refreshCost.isPending ? 'Refreshing…' : 'Refresh cost data'}
      </button>
      <button
        type="button"
        disabled={!data}
        onClick={() => data && downloadUsageCsv(data)}
        className="rounded border border-steel-300 px-2 py-0.5 text-xs text-navy-800 hover:border-navy-400 disabled:opacity-50"
      >
        Download CSV
      </button>
    </>
  );

  return (
    <Panel
      testId="usage"
      title="Usage by person"
      description={
        <>
          {data && `${data.start} to ${data.end}. `}
          Engineer-tool data starts 21 August 2026 and is kept for a year; portal data starts
          12 October 2026; cost starts 1 October 2026. Cost comes from the AWS bill, copied
          daily, so it can lag by a day.
        </>
      }
      actions={actions}
    >
      {data && !data.cost_error && (
        <p
          className="mb-4 rounded-lg bg-steel-50 px-4 py-3 text-sm text-navy-900"
          data-testid="cost-total"
        >
          {filtered ? 'Model spend matching this filter' : 'Model spend in this period'}:{' '}
          <strong>{fmtUsd(totalCost)}</strong>
          <span className="text-xs text-steel-500">
            {!filtered &&
              ` (people ${fmtUsd(peopleCost)}, portal ${fmtUsd(data.portal_cost ?? 0)}, other ${fmtUsd(data.other_cost ?? 0)})`}
            {modelVendor(model) &&
              ` (all ${modelVendor(model) === 'claude' ? 'Claude' : 'GPT'} models: the bill doesn't split cost by model)`}
            {data.cost_as_of && `. Bill copied ${fmtTs(data.cost_as_of)}.`}
          </span>
        </p>
      )}
      {data?.cost_error && (
        <p role="alert" className="mb-2 text-sm text-red-700">
          Couldn&apos;t load cost: {data.cost_error}
        </p>
      )}
      {refreshCost.error && (
        <p role="alert" className="mb-2 text-sm text-red-700">
          {refreshCost.error instanceof Error ? refreshCost.error.message : 'Refresh failed.'}
        </p>
      )}
      {report.error && (
        <p role="alert" className="mb-2 text-sm text-red-700">
          {report.error instanceof Error ? report.error.message : 'Could not load usage.'}
        </p>
      )}

      {report.isLoading && <p className="text-sm text-steel-500">Loading…</p>}

      {data && (
        <div className="flex flex-col gap-6">
          <div>
            <SubHeading
              title="Claude Desktop, Cowork, Claude Code and Codex"
              note={
                <>
                  Requests from AWS CloudTrail. AWS does not record tokens per person for these
                  tools.
                  {data.tools_as_of && ` As of ${fmtTs(data.tools_as_of)}; refreshed hourly.`}
                </>
              }
            />
            {data.tools_error ? (
              <p role="alert" className="text-sm text-red-700">
                Couldn't load engineer-tool usage: {data.tools_error}
              </p>
            ) : (
              <UsageTable
                rows={tools}
                problemLabel="Refused"
                showTokens={false}
                cost={data.cost_error ? undefined : data.cost_by_vendor}
                filtered={filtered}
              />
            )}
          </div>
          <div className="border-t border-steel-100 pt-5">
            <SubHeading
              title="Portal"
              note={
                <>
                  Every model call made through the portal, with tokens. The bill shows portal
                  cost only as one total
                  {data.portal_cost !== null && `: ${fmtUsd(data.portal_cost)} in this period`}.
                </>
              }
            />
            <UsageTable rows={portal} problemLabel="Failed" showTokens filtered={filtered} />
          </div>
        </div>
      )}
    </Panel>
  );
}

function UsageTable({
  rows,
  problemLabel,
  showTokens,
  cost,
  filtered = false,
}: {
  rows: PersonTotals[];
  problemLabel: string;
  showTokens: boolean;
  cost?: Record<string, Record<string, number>>;
  filtered?: boolean;
}): ReactNode {
  const cols = (showTokens ? 7 : 5) + (cost ? 3 : 0);
  const usd = (person: string, vendor?: string): string => {
    const v = cost?.[person];
    if (!v) return '—';
    const amount = vendor ? (v[vendor] ?? 0) : Object.values(v).reduce((a, b) => a + b, 0);
    return fmtUsd(amount);
  };
  return (
    <div className="overflow-x-auto rounded-lg border border-steel-200">
      <table className="w-full text-sm">
        <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
          <tr>
            <th className="px-3 py-2">User</th>
            <th className="px-3 py-2 text-right">Requests</th>
            {cost && <th className="px-3 py-2 text-right">Claude (Desktop, Cowork, Claude Code)</th>}
            {cost && <th className="px-3 py-2 text-right">GPT (Codex)</th>}
            {cost && <th className="px-3 py-2 text-right">Total cost</th>}
            <th className="px-3 py-2 text-right">{problemLabel}</th>
            {showTokens && <th className="px-3 py-2 text-right">Input tokens</th>}
            {showTokens && <th className="px-3 py-2 text-right">Output tokens</th>}
            <th className="px-3 py-2">Models</th>
            <th className="px-3 py-2">Last used</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.person} className="border-t border-steel-100">
              <td className="px-3 py-2 font-medium text-navy-900">{t.person}</td>
              <td className="px-3 py-2 text-right">{fmtN(t.requests)}</td>
              {cost && <td className="px-3 py-2 text-right">{usd(t.person, 'claude')}</td>}
              {cost && <td className="px-3 py-2 text-right">{usd(t.person, 'gpt')}</td>}
              {cost && (
                <td className="px-3 py-2 text-right font-medium">{usd(t.person)}</td>
              )}
              <td className="px-3 py-2 text-right">
                {t.problems > 0 ? <span className="text-red-700">{fmtN(t.problems)}</span> : 0}
              </td>
              {showTokens && <td className="px-3 py-2 text-right">{fmtN(t.input)}</td>}
              {showTokens && <td className="px-3 py-2 text-right">{fmtN(t.output)}</td>}
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {[...t.models.keys()].map((m) => (
                    <span key={m} className="rounded bg-steel-100 px-1.5 py-0.5 text-xs text-steel-700">
                      {m}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2 text-steel-600">{t.last ? fmtTs(t.last) : '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td className="px-3 py-3 text-steel-500" colSpan={cols}>
                {filtered ? 'No usage matches this filter.' : 'No usage recorded in this period.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Suspensions(): ReactNode {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');

  const list = useQuery({
    queryKey: ['admin-suspensions'],
    queryFn: () => api.get<Suspension[]>('/admin/suspensions'),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-suspensions'] });
  const suspend = useMutation({
    mutationFn: () => api.post<Suspension>('/admin/suspensions', { email, reason }),
    onSuccess: () => {
      setEmail('');
      setReason('');
      refresh();
    },
  });
  const restore = useMutation({
    mutationFn: (target: string) =>
      api.delete<void>(`/admin/suspensions/${encodeURIComponent(target)}`),
    onSuccess: refresh,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim() && reason.trim()) suspend.mutate();
  };
  const error = suspend.error ?? restore.error;

  return (
    <Panel
      testId="suspensions"
      title="Suspended users"
      description="Stops a person's portal access on their next request, even if they are signed in. Claude Desktop and Codex are suspended separately in AWS."
    >
      <form onSubmit={submit} className="mb-3 flex flex-wrap gap-2">
        <input
          aria-label="Email to suspend"
          type="email"
          placeholder="name@tridsys.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="min-w-56 flex-1 rounded border border-steel-300 px-2 py-1 text-sm"
        />
        <input
          aria-label="Reason"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="min-w-56 flex-1 rounded border border-steel-300 px-2 py-1 text-sm"
        />
        <button
          type="submit"
          disabled={!email.trim() || !reason.trim() || suspend.isPending}
          className="rounded bg-red-700 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
        >
          Suspend
        </button>
      </form>
      {error && (
        <p role="alert" className="mb-2 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Something went wrong.'}
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-steel-200">
        <table className="w-full text-sm">
          <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
            <tr>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Suspended by</th>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((s) => (
              <tr key={s.email} className="border-t border-steel-100">
                <td className="px-3 py-2 font-medium text-navy-900">{s.email}</td>
                <td className="px-3 py-2 text-steel-700">{s.reason}</td>
                <td className="px-3 py-2 text-steel-600">{s.suspended_by}</td>
                <td className="px-3 py-2 text-steel-600">{fmtTs(s.suspended_at)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => restore.mutate(s.email)}
                    disabled={restore.isPending}
                    className="rounded border border-steel-300 px-2 py-0.5 text-xs text-navy-800 hover:border-navy-400"
                  >
                    Restore
                  </button>
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-steel-500" colSpan={5}>
                  Nobody is suspended.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export function Dashboard(): ReactNode {
  const query = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.get<Overview>('/admin/overview'),
    retry: false,
  });

  const forbidden = query.error instanceof ApiError && query.error.status === 403;
  const data = query.data;

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="admin-dashboard">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">Dashboard</h2>
        <p className="mt-1 text-sm text-steel-600">
          Who uses the platform, what it costs, and who can get in. Model-level
          metering and infrastructure health are in the AWS consoles at the bottom.
        </p>
      </header>

      {forbidden && (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          The dashboard is restricted to platform administrators.
        </div>
      )}

      {query.isLoading && <p className="text-sm text-steel-500">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Active users" value={data.users.length} />
            <StatTile label="Audit events" value={data.total_events} />
            <StatTile
              label="Audit chain"
              value={
                <span className={data.chain_valid ? 'text-emerald-700' : 'text-red-700'}>
                  {data.chain_valid ? 'Verified' : 'TAMPERED'}
                </span>
              }
            />
            <StatTile label="Model" value={data.model_id} />
          </div>

          <Panel
            title="Portal users"
            description="People who have used the portal, from the audit log."
          >
            <div className="overflow-x-auto rounded-lg border border-steel-200">
              <table className="w-full text-sm">
                <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
                  <tr>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Groups</th>
                    <th className="px-3 py-2 text-right">Events</th>
                    <th className="px-3 py-2 text-right">Denied</th>
                    <th className="px-3 py-2">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.actor} className="border-t border-steel-100">
                      <td className="px-3 py-2 font-medium text-navy-900">{u.actor}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {u.groups.map((g) => (
                            <span
                              key={g}
                              className="rounded bg-steel-100 px-1.5 py-0.5 text-xs text-steel-700"
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">{u.events}</td>
                      <td className="px-3 py-2 text-right">
                        {u.denied > 0 ? (
                          <span className="text-red-700">{u.denied}</span>
                        ) : (
                          u.denied
                        )}
                      </td>
                      <td className="px-3 py-2 text-steel-600">{fmtTs(u.last_seen)}</td>
                    </tr>
                  ))}
                  {data.users.length === 0 && (
                    <tr>
                      <td className="px-3 py-3 text-steel-500" colSpan={5}>
                        No activity recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>

          <Usage />

          <Suspensions />

          <Panel title="Access groups" description="Entra groups and what each one grants in the portal.">
            <div className="overflow-x-auto rounded-lg border border-steel-200">
              <table className="w-full text-sm">
                <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
                  <tr>
                    <th className="px-3 py-2">Entra source</th>
                    <th className="px-3 py-2">Group</th>
                    <th className="px-3 py-2">Grants</th>
                  </tr>
                </thead>
                <tbody>
                  {data.group_mappings.map((m) => (
                    <tr key={m.source + m.name} className="border-t border-steel-100">
                      <td className="px-3 py-2 font-mono text-xs text-steel-600">{m.source}</td>
                      <td className="px-3 py-2 text-navy-900">{m.name}</td>
                      <td className="px-3 py-2">
                        {m.admin ? (
                          <>
                            <span className="rounded bg-gold-100 px-1.5 py-0.5 text-xs font-medium text-gold-800">
                              Platform Administrator
                            </span>
                            <span className="block mt-0.5 text-xs text-steel-500">
                              Everything: Dashboard, Audit, Retrieval, Connectors
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-steel-600">Member access</span>
                            <span className="block mt-0.5 text-xs text-steel-500">
                              Chat + Knowledge, filtered to their document permissions
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-steel-500">{data.admin_roles_note}</p>
          </Panel>

          <Panel title="AWS consoles" description="Requires an AWS console login (DevOps).">
            <div className="grid gap-3 sm:grid-cols-2">
              {AWS_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-steel-200 bg-white p-4 hover:border-navy-400"
                >
                  <p className="font-medium text-navy-800">{l.label} ↗</p>
                  <p className="mt-0.5 text-xs text-steel-500">{l.note}</p>
                </a>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

export default Dashboard;
